import type { RouteLocationNormalized } from "vue-router";
import { useAuth0 } from "@auth0/auth0-vue";
import { abortNavigation, navigateTo } from "#app";
import { useJwtStore } from "@/store/jwt";
import { clearSessionData, setSessionData } from "~/utils/SetSessionData";
import { IsUnauthenticatedRoute } from "~/utils/IsUnauthenticatedRoute";
import { until } from "@/utils/Async";
import { isSessionStale } from "@/constants/session";
import { useLoadingStore } from "@/store/loadingStore";
import { useHubspotSync } from "~/composables/useHubspotSync";
import { useCookieConsentStore } from "@/store/cookieConsentStore";
import { upNavRoutePaths } from "~/constants/upNavRouteConfig";
import {
  extractEmailFromError,
  AUTH_GRANTS_CLAIM_KEY,
  getAuthGrantsFromClaims,
  hasRequiredAuthGrants,
  saveEmailToLocalStorage,
  setUserOrgFromLocalStorage,
} from "~/composables/useAuthMiddlewareUtils";

// Exposed for tests to override when import.meta.client is false
let __TEST_FORCE_CLIENT = false;

export function __setTestForceClient(val: boolean) {
  __TEST_FORCE_CLIENT = val;
}

const authMiddleware = async (context: RouteLocationNormalized) => {
  if (!import.meta.client && !__TEST_FORCE_CLIENT) return;

  const loadingStore = useLoadingStore();
  const taskId = `auth-middleware-${Date.now()}`;
  loadingStore.startTask(taskId);

  try {
    // Don't check authentication for unprotected routes
    if (IsUnauthenticatedRoute(context.path)) {
      loadingStore.endTask(taskId);
      return;
    }

    const auth0 = useAuth0();
    if (auth0.isLoading.value) {
      await until(() => !auth0.isLoading.value);
    }

    // Check for Auth0 callback errors (e.g., access_denied from Auth0 Actions/Rules)
    if (auth0.error.value) {
      const email = extractEmailFromError(auth0.error.value.error_description);
      saveEmailToLocalStorage(email);

      // eslint-disable-next-line no-console
      console.error("Auth0 authentication error:", auth0.error.value);
      loadingStore.endTask(taskId);
      await navigateTo(upNavRoutePaths.mentionUpsell.path, { external: true });
      return abortNavigation();
    }

    await auth0.checkSession();
    const isAuthenticated = auth0.isAuthenticated.value;

    if (!isAuthenticated) {
      // Save attempted route to localStorage before attempting to login, unless it's the '/logout' route
      // and Avoid invalid attempted route ("/") that Auth0 may set during login redirect
      if (
        context.fullPath !== upNavRoutePaths.logout.path &&
        context.fullPath !== upNavRoutePaths.root.path
      ) {
        localStorage.setItem("attemptedRoute", context.fullPath);
      }

      loadingStore.endTask(taskId);
      await auth0.loginWithRedirect();
      return abortNavigation();
    } else {
      const jwtStore = useJwtStore();

      // Disabled: session version gating (kept for future reference)
      // // Session version gating (runs before fetching new JWT)
      // const runtime = useRuntimeConfig();
      // const requiredVersion = (runtime as any).public?.REQUIRED_SESSION_VERSION; // disabled if not set
      // if (requiredVersion) {
      //   const versionCookie = useCookie<string | undefined>(
      //     SESSION_VERSION_COOKIE,
      //     {
      //       maxAge: 60 * 60 * 24 * 365,
      //       sameSite: "lax",
      //       secure: true,
      //     },
      //   );
      //   // Force exactly once per version only if a session token exists and cookie mismatch
      //   if (jwtStore.sessionJwt && versionCookie.value !== requiredVersion) {
      //     jwtStore.clearSessionJwt();
      //     // Stamp new version immediately to avoid loop on next navigation before login completes
      //     versionCookie.value = requiredVersion;
      //     await navigateTo("/logout", { external: true });
      //     return abortNavigation();
      //   }
      // }

      const claims = auth0.idTokenClaims?.value as
        | Record<string, unknown>
        | undefined;
      const email = typeof claims?.email === "string" ? claims.email : "";
      const authGrants = getAuthGrantsFromClaims(claims);
      saveEmailToLocalStorage(email);

      // Enforce grants check only if the grant claim exists in JWT
      const grantsClaimExists = claims && AUTH_GRANTS_CLAIM_KEY in claims;
      if (grantsClaimExists && !hasRequiredAuthGrants(authGrants)) {
        // User does not have the necessary grants (or grants claim is empty)
        loadingStore.endTask(taskId);
        await navigateTo(upNavRoutePaths.mentionUpsell.path, {
          external: true,
        });
        return abortNavigation();
      }

      // Validate session if: no JWT, no user data, OR session is stale (>30 min)
      if (
        !jwtStore.sessionJwt ||
        !localStorage.getItem("user") ||
        isSessionStale()
      ) {
        const jwt = await auth0.getAccessTokenSilently();
        jwtStore.updateSessionJwt(jwt);

        const response = await setSessionData();

        if (response === "NoApplicationAccess") {
          if (context.fullPath !== upNavRoutePaths.noAccess.path) {
            loadingStore.endTask(taskId);
            await navigateTo(upNavRoutePaths.noAccess.path, {
              external: true,
            });
          }
          return abortNavigation();
        } else if (response === "TokenRevoked") {
          loadingStore.endTask(taskId);

          // Auth0 session was terminated (backchannel logout or timeout)
          // Clear local state and redirect to login via standard Auth0 flow
          jwtStore.clearSessionJwt();
          clearSessionData();

          await auth0.loginWithRedirect();
          return abortNavigation();
        } else if (response === "NotAuthorized") {
          if (context.fullPath !== upNavRoutePaths.expired.path) {
            loadingStore.endTask(taskId);
            await navigateTo(upNavRoutePaths.expired.path, {
              external: true,
            });
          }
          return abortNavigation();
        } else {
          const attemptedRoute = localStorage.getItem("attemptedRoute");

          setUserOrgFromLocalStorage();

          // Track user login in HubSpot (fire-and-forget; failures must not block auth)
          const { trackUserLoggedIn } = useHubspotSync();
          const cookieConsentStore = useCookieConsentStore();

          const isFunctionalCookiesEnabled =
            cookieConsentStore.areFunctionalCookiesEnabled;
          if (isFunctionalCookiesEnabled) {
            trackUserLoggedIn(context.fullPath);
          }

          if (attemptedRoute) {
            localStorage.removeItem("attemptedRoute");
            loadingStore.endTask(taskId);
            return await navigateTo(attemptedRoute);
          } else if (context.fullPath === upNavRoutePaths.root.path) {
            loadingStore.endTask(taskId);
            return await navigateTo(upNavRoutePaths.mentionSearch.path);
          }
        }
      }

      const userEmail = JSON.parse(localStorage.getItem("user") || "{}").email;
      saveEmailToLocalStorage(userEmail);
    }

    setUserOrgFromLocalStorage();

    if (context.fullPath === upNavRoutePaths.root.path) {
      loadingStore.endTask(taskId);
      return await navigateTo(upNavRoutePaths.mentionSearch.path);
    }

    loadingStore.endTask(taskId);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error in authMiddleware", error);
    loadingStore.endTask(taskId);
    // redirect to login page
    await navigateTo(upNavRoutePaths.logout.path, {
      external: true,
    });
  }
};

export default authMiddleware;
